import { Fragment } from 'react';

import { cn } from '@holistic/components';
import { Check, ChevronRight, Timer } from '@holistic/components/icons';

import type { StepLabel } from './constants';

type TalepStepperProps = {
  steps: StepLabel[];
  /** 0'dan başlayan aktif adım; tamamlanınca steps.length olur. */
  active: number;
};

/** Akışın üstündeki adım şeridi ve sağdaki süre rozeti. */
const TalepStepper = ({ steps, active }: TalepStepperProps) => {
  const total = steps.length;
  // Akış tamamlandığında `active` son adımın da ötesine geçer.
  const isCompleted = active >= total;
  const currentIndex = Math.min(active, total - 1);
  const current = steps[currentIndex];

  return (
    <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
      {/* Dar ekran: adım sayacı + bölmeli ilerleme çubuğu. Altı adımlık şerit
          telefona sığmadığı için yatay kaydırma gerekiyordu; bunun yerine
          "neredeyim / kaç adım var" tek bakışta okunuyor. */}
      <div className="flex flex-col gap-2.5 lg:hidden">
        <div className="flex items-baseline justify-between gap-4">
          <span className="min-w-0 leading-tight">
            <span
              className={cn(
                'block truncate text-sm font-semibold',
                isCompleted ? 'text-gray-900' : 'text-primary'
              )}
            >
              {isCompleted ? 'Talebiniz oluşturuldu' : current.label}
            </span>
            <span className="block truncate text-xs text-gray-400">
              {isCompleted ? 'Tüm adımlar tamamlandı' : current.description}
            </span>
          </span>

          <span className="shrink-0 text-xs font-medium text-gray-400 tabular-nums">
            {isCompleted
              ? `${total}/${total}`
              : `Adım ${currentIndex + 1}/${total}`}
          </span>
        </div>

        <div
          role="progressbar"
          aria-label="Tamir talebi adımları"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={Math.min(active + 1, total)}
          className="flex gap-1"
        >
          {steps.map((step, index) => (
            <span
              key={step.label}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                index <= active ? 'bg-primary' : 'bg-gray-200'
              )}
            />
          ))}
        </div>
      </div>

      {/* lg ve üzeri: adımların tamamı görünür. Açıklama alt satırı yalnızca
          xl'den itibaren gösteriliyor ki şerit sığsın. */}
      <ol className="hidden min-w-0 flex-1 items-center gap-1 lg:flex">
        {steps.map((step, index) => {
          const isDone = index < active;
          const isCurrent = index === active;

          return (
            <Fragment key={step.label}>
              {index > 0 && (
                <ChevronRight
                  aria-hidden
                  className="size-4 shrink-0 text-gray-300"
                />
              )}
              <li className="flex min-w-0 shrink items-center gap-2.5 pr-2 xl:pr-3">
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors',
                    isDone || isCurrent
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-400'
                  )}
                >
                  {isDone ? (
                    <Check className="size-4" strokeWidth={3} />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="min-w-0 leading-tight">
                  <span
                    className={cn(
                      'block truncate text-sm font-semibold',
                      isCurrent ? 'text-primary' : 'text-gray-900'
                    )}
                  >
                    {step.label}
                  </span>
                  <span className="hidden truncate text-xs text-gray-400 xl:block">
                    {step.description}
                  </span>
                </span>
              </li>
            </Fragment>
          );
        })}
      </ol>

      <div className="bg-primary/5 flex w-fit shrink-0 items-center gap-3 rounded-xl px-4 py-2.5">
        <span className="bg-primary/10 flex size-9 items-center justify-center rounded-lg">
          <Timer className="text-primary size-4" />
        </span>
        <span className="text-sm leading-tight">
          <span className="block font-semibold text-gray-900">3 dakikada</span>
          <span className="text-gray-500">tamir talebi oluşturun.</span>
        </span>
      </div>
    </div>
  );
};

export default TalepStepper;
